import moment from 'moment';

import type {
  ErpAiStudentFinancePortraitQuery,
  ErpAiStudentPortraitQuery,
} from 'gqlhooks';
import { PATHS } from 'router/config';
import { dashboardLink } from 'core/utils/dashboardDeepLink';
import { encode as encodeStudentLink } from 'core/utils/studentDeepLink';
import type { StudentTabId } from 'core/utils/studentAlerts';

/**
 * ТЗ 0104, §3.5 / §4 «Агент 3» — чистая модель ИИ-портрета студента:
 * статусы и опрос, тона серьёзности, коды отказов и сбоев, ссылки
 * доказательств. Без React — тестируется напрямую. Импортируется прямым
 * путём (`core/hooks/AiPortrait/aiPortraitModel`), не через барабан
 * `core/hooks`: там имена потоков столкнулись бы.
 */

// ── Типы ответа ────────────────────────────────────────────────────────────

export type AiStudentPortrait = ErpAiStudentPortraitQuery['aiStudentPortrait'];
export type AiPortraitItem = AiStudentPortrait['risks'][number];
export type AiEvidenceRef = AiPortraitItem['evidence'][number];
export type AiPortraitFact = AiStudentPortrait['about'][number];
export type AiPortraitCheckpoint = AiStudentPortrait['checkpoints'][number];
export type AiStudentFinancePortrait = NonNullable<
  ErpAiStudentFinancePortraitQuery['aiStudentFinancePortrait']
>;

// ── Статусы ────────────────────────────────────────────────────────────────

/** Статусы портрета (строки сервера — graphene-енумов нет). */
export const AI_PORTRAIT_STATUSES = [
  'none',
  'queued',
  'running',
  'ready',
  'failed',
] as const;
export type AiPortraitStatus = typeof AI_PORTRAIT_STATUSES[number];

export const isAiPortraitStatus = (value: unknown): value is AiPortraitStatus =>
  (AI_PORTRAIT_STATUSES as readonly unknown[]).includes(value);

/** Как часто портрет перечитывается, пока он в очереди или пишется. */
export const AI_PORTRAIT_POLL_MS = 4000;

/**
 * Портрет «в движении» — только по нему идёт опрос. `waiting_human` прогона
 * сервер показывает как `running`, отдельного статуса у портрета нет.
 */
export const aiPortraitPolled = (status: string | null | undefined): boolean =>
  status === 'queued' || status === 'running';

/**
 * Есть что показать: сводка, хоть один раздел или пункт. У `failed` и
 * `running` прежний портрет остаётся на экране — сервер отдаёт его поля
 * вместе с новым статусом.
 */
export const aiPortraitHasContent = (
  portrait: AiStudentPortrait | null | undefined,
): boolean =>
  Boolean(
    portrait &&
      (portrait.summary ||
        portrait.learning ||
        portrait.engagement ||
        portrait.sentimentTrend ||
        portrait.communication ||
        portrait.risks.length ||
        portrait.openNeeds.length ||
        portrait.nextActions.length ||
        portrait.about?.length ||
        portrait.strengths?.length ||
        portrait.concerns?.length ||
        portrait.checkpoints?.length),
  );

/**
 * ТЗ 0120 — у портрета есть структура v3 (чекпоинты, «кто это», плюсы и
 * минусы). Портрет v2 приходит с пустыми списками: экран предлагает
 * обновить его, а не рисует пустые сетки.
 */
export const aiPortraitHasStructure = (
  portrait: AiStudentPortrait | null | undefined,
): boolean =>
  Boolean(
    portrait &&
      (portrait.checkpoints?.length ||
        portrait.about?.length ||
        portrait.strengths?.length ||
        portrait.concerns?.length),
  );

/** То же для финансового приложения. */
export const aiFinancePortraitHasContent = (
  finance: AiStudentFinancePortrait | null | undefined,
): boolean => Boolean(finance && (finance.summary || finance.items.length));

/**
 * Текст модели без разметки — для мини-карточки, где markdown не рисуется
 * (там три строки сводки): `**жирный**` → «жирный», `[текст](url)` →
 * «текст», маркеры заголовков, списков и цитат — прочь, переносы — в пробел.
 */
export const aiPortraitPlainText = (text: string | null | undefined): string =>
  (text ?? '')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^\s{0,3}(#{1,6}|>|[-*+]|\d+[.)])\s+/gm, '')
    .replace(/(\*\*|__|\*|`)/g, '')
    .replace(/\s+/g, ' ')
    .trim();

// ── Разделы ────────────────────────────────────────────────────────────────

/** Текстовые разделы портрета — порядок на экране. */
export const AI_PORTRAIT_TEXT_SECTIONS = [
  'learning',
  'engagement',
  'sentimentTrend',
  'communication',
] as const;
export type AiPortraitTextSection = typeof AI_PORTRAIT_TEXT_SECTIONS[number];

/** Списки портрета — порядок на экране. */
export const AI_PORTRAIT_LISTS = ['risks', 'openNeeds', 'nextActions'] as const;
export type AiPortraitList = typeof AI_PORTRAIT_LISTS[number];

// ── Серьёзность ────────────────────────────────────────────────────────────

export const AI_PORTRAIT_SEVERITIES = ['high', 'normal', 'low'] as const;
export type AiPortraitSeverity = typeof AI_PORTRAIT_SEVERITIES[number];

export const isAiPortraitSeverity = (
  value: unknown,
): value is AiPortraitSeverity =>
  (AI_PORTRAIT_SEVERITIES as readonly unknown[]).includes(value);

/**
 * Тон метки серьёзности — из палитры `TagTone` ERP: `high` — красный,
 * `normal` — оранжевый, `low` — серый. Незнакомая или пустая — серый.
 */
export type AiPortraitTone = 'danger' | 'warn' | 'neutral';

export const aiPortraitSeverityTone = (
  severity: string | null | undefined,
): AiPortraitTone => {
  if (severity === 'high') return 'danger';
  if (severity === 'normal') return 'warn';
  return 'neutral';
};

/** Ранг для сортировки: `high` выше всех, без серьёзности — ниже всех. */
const severityRank = (severity: string | null | undefined): number => {
  const at = (AI_PORTRAIT_SEVERITIES as readonly string[]).indexOf(
    severity ?? '',
  );
  return at === -1 ? AI_PORTRAIT_SEVERITIES.length : at;
};

/**
 * Главный риск для мини-карточки — самый серьёзный; при равной серьёзности
 * первый в порядке модели. Нет рисков — `null`.
 */
export const aiPortraitTopRisk = (
  risks: ReadonlyArray<AiPortraitItem> | null | undefined,
): AiPortraitItem | null =>
  (risks ?? []).reduce<AiPortraitItem | null>(
    (best, item) =>
      best === null || severityRank(item.severity) < severityRank(best.severity)
        ? item
        : best,
    null,
  );

/**
 * ТЗ 0120 — пункты по серьёзности (`high` → `normal` → `low` → без неё); при
 * равной — порядок модели. Для таблицы рисков.
 */
export const aiPortraitBySeverity = (
  items: ReadonlyArray<AiPortraitItem> | null | undefined,
): AiPortraitItem[] =>
  (items ?? [])
    .map((item, index) => ({ item, index }))
    .sort(
      (a, b) =>
        severityRank(a.item.severity) - severityRank(b.item.severity) ||
        a.index - b.index,
    )
    .map(({ item }) => item);

/** Тон блока рисков: есть высокий — красный, иначе оранжевый. */
export const aiPortraitRisksTone = (
  risks: ReadonlyArray<AiPortraitItem> | null | undefined,
): 'danger' | 'warn' =>
  (risks ?? []).some((item) => item.severity === 'high') ? 'danger' : 'warn';

/**
 * ТЗ 0120 — главный плюс для мини-карточки: тот же выбор, что у риска
 * (серьёзность, при равной — порядок модели). Нет плюсов — `null`.
 */
export const aiPortraitTopStrength = (
  strengths: ReadonlyArray<AiPortraitItem> | null | undefined,
): AiPortraitItem | null => aiPortraitTopRisk(strengths);

// ── Чекпоинты (ТЗ 0120) ────────────────────────────────────────────────────

/** Чекпоинты портрета — фиксированный порядок сервера и экрана. */
export const AI_PORTRAIT_CHECKPOINTS = [
  'motivation',
  'funding_need',
  'tech_level',
  'engagement',
  'attendance',
  'payment',
] as const;
export type AiPortraitCheckpointKey = typeof AI_PORTRAIT_CHECKPOINTS[number];

export const isAiPortraitCheckpointKey = (
  value: unknown,
): value is AiPortraitCheckpointKey =>
  (AI_PORTRAIT_CHECKPOINTS as readonly unknown[]).includes(value);

/** Статусы чекпоинта: зелёная галочка, жёлтое «внимание», красный крест, пунктир. */
export const AI_CHECKPOINT_STATUSES = ['ok', 'warn', 'bad', 'unknown'] as const;
export type AiCheckpointStatus = typeof AI_CHECKPOINT_STATUSES[number];

/** Незнакомый или пустой статус — «неизвестно», не «хорошо». */
export const aiCheckpointStatus = (
  value: string | null | undefined,
): AiCheckpointStatus =>
  (AI_CHECKPOINT_STATUSES as readonly unknown[]).includes(value)
    ? (value as AiCheckpointStatus)
    : 'unknown';

/**
 * Чекпоинт «Оплата» у роли без финансов: сервер пишет `unknown` — это не
 * «нет данных», а «нет доступа» (своя подпись и значок замка).
 */
export const aiCheckpointNoAccess = (
  key: string,
  status: string | null | undefined,
  canSeeFinance: boolean,
): boolean =>
  key === 'payment' &&
  !canSeeFinance &&
  aiCheckpointStatus(status) === 'unknown';

/** Тон палитры `TagTone` ERP для статуса чекпоинта. */
export type AiCheckpointTone = 'ok' | 'warn' | 'danger' | 'neutral';

const CHECKPOINT_TONES: Readonly<
  Record<AiCheckpointStatus, AiCheckpointTone>
> = {
  ok: 'ok',
  warn: 'warn',
  bad: 'danger',
  unknown: 'neutral',
};

export const aiCheckpointTone = (
  status: string | null | undefined,
): AiCheckpointTone => CHECKPOINT_TONES[aiCheckpointStatus(status)];

/**
 * Чекпоинты в порядке экрана: сначала шесть известных (пропущенный сервером
 * — «неизвестно» без текста, чтобы ряд значков всегда был полным), затем
 * незнакомые ключи, как пришли. Пустой список (портрет v2) — пустой.
 */
export const aiPortraitCheckpointsOrdered = (
  checkpoints: ReadonlyArray<AiPortraitCheckpoint> | null | undefined,
): AiPortraitCheckpoint[] => {
  const list = checkpoints ?? [];
  if (!list.length) return [];
  const known = AI_PORTRAIT_CHECKPOINTS.map(
    (key): AiPortraitCheckpoint =>
      list.find((item) => item.key === key) ?? {
        __typename: 'AiPortraitCheckpointType',
        key,
        status: 'unknown',
        text: '',
        evidence: [],
      },
  );
  const extra = list.filter((item) => !isAiPortraitCheckpointKey(item.key));
  return [...known, ...extra];
};

const CHECKPOINT_RANK: Readonly<Record<AiCheckpointStatus, number>> = {
  bad: 0,
  warn: 1,
  ok: 2,
  unknown: 3,
};

/** Худшие вперёд (`bad` → `warn` → `ok` → `unknown`); при равенстве — порядок экрана. */
export const aiCheckpointsWorstFirst = (
  checkpoints: ReadonlyArray<AiPortraitCheckpoint> | null | undefined,
): AiPortraitCheckpoint[] =>
  aiPortraitCheckpointsOrdered(checkpoints)
    .map((item, index) => ({ item, index }))
    .sort(
      (a, b) =>
        CHECKPOINT_RANK[aiCheckpointStatus(a.item.status)] -
          CHECKPOINT_RANK[aiCheckpointStatus(b.item.status)] ||
        a.index - b.index,
    )
    .map(({ item }) => item);

/**
 * Худший чекпоинт, о котором стоит сказать (`bad` или `warn`), — для значка в
 * полосе контекста коммуникатора. Всё хорошо или неизвестно — `null`.
 */
export const aiPortraitWorstCheckpoint = (
  checkpoints: ReadonlyArray<AiPortraitCheckpoint> | null | undefined,
): AiPortraitCheckpoint | null => {
  const [worst] = aiCheckpointsWorstFirst(checkpoints);
  if (!worst) return null;
  const status = aiCheckpointStatus(worst.status);
  return status === 'bad' || status === 'warn' ? worst : null;
};

// ── Отказы и сбои ──────────────────────────────────────────────────────────

/**
 * Коды отказа «Odśwież portret» (`extensions.code`) — у каждого своя фраза
 * `aiPortrait.refusal.<код>`; незнакомый — `generic`.
 */
export const AI_PORTRAIT_REFUSALS = [
  'ai_disabled',
  'already_running',
  'budget_exceeded',
  'forbidden',
  'not_found',
] as const;
export type AiPortraitRefusal = typeof AI_PORTRAIT_REFUSALS[number];

export const aiPortraitRefusalKey = (code: string): string =>
  (AI_PORTRAIT_REFUSALS as readonly string[]).includes(code)
    ? `aiPortrait.refusal.${code}`
    : 'aiPortrait.refusal.generic';

/**
 * Код упавшего прогона (`error` при `status: failed`) — фраза
 * `aiPortrait.failure.<код>`; незнакомый или пустой — `generic`.
 */
export const AI_PORTRAIT_FAILURES = [
  'budget_exceeded',
  'ai_disabled',
  'not_found',
  'internal_error',
] as const;

export const aiPortraitFailureKey = (code: string | null | undefined): string =>
  code && (AI_PORTRAIT_FAILURES as readonly string[]).includes(code)
    ? `aiPortrait.failure.${code}`
    : 'aiPortrait.failure.generic';

// ── Доказательства ─────────────────────────────────────────────────────────

/** Виды ссылок-доказательств (`AiEvidenceRefType.kind`). */
export const AI_EVIDENCE_KINDS = [
  'message',
  'sms',
  'note',
  'task',
  'attendance',
  'mark',
  'survey',
  'agreement',
  'payment',
  'invoice',
  'funding',
  // ТЗ 0120: сделка и разбор звонка Aplora — карточка лида.
  'aplora_lead',
  'aplora_call',
] as const;
export type AiEvidenceKind = typeof AI_EVIDENCE_KINDS[number];

export const isAiEvidenceKind = (value: unknown): value is AiEvidenceKind =>
  (AI_EVIDENCE_KINDS as readonly unknown[]).includes(value);

/**
 * Что из ссылок открывается у этого сотрудника. Ссылка в маршрут, которого у
 * роли нет, — 404; такой чип рисуется простым текстом.
 */
export interface IAiEvidenceAccess {
  /** Таск-трекер (`/dashboard`): `TASK_TRACKER_ROLES`, у методиста его нет. */
  canOpenTasks: boolean;
  /** Вкладки карточки студента — флаги `useStudentPermissions`. */
  canSeeCommunicationTab: boolean;
  canSeeSurveysTab: boolean;
  canSeeAgreementsTab: boolean;
  canSeePaymentsTab: boolean;
  canSeeInvoicesTab: boolean;
  /** ТЗ 0120: раздел «Лиды» (`LEAD_ROLES`) — карточка лида с вкладкой Aplora. */
  canOpenLeads: boolean;
}

/**
 * ТЗ 0120: карточка лида на вкладке «Aplora» — `?lead=<id>&tab=aplora`
 * (параметры раздела «Лиды», `leadsModel`).
 */
export const aiLeadAploraHref = (leadId: string): string => {
  const qs = new URLSearchParams({ lead: leadId, tab: 'aplora' }).toString();
  return `${PATHS.leads}?${qs}`;
};

/** Вкладка карточки студента: адрес из `PATHS` и кодировщика её состояния. */
export const aiStudentTabHref = (
  studentId: string,
  tab: StudentTabId,
): string => {
  const qs = encodeStudentLink({
    tab,
    paymentsAgreement: 'all',
    paymentsStatus: 'all',
  }).toString();
  const base = PATHS.studentDetail.replace(':id', studentId);
  return qs ? `${base}?${qs}` : base;
};

/**
 * Куда ведёт чип доказательства; `null` — внутренней ссылки нет (простой
 * текст).
 *
 *  - `message` / `sms` / `note` → вкладка «Общение» карточки студента: там
 *    переписка Slack и почты, SMS и заметки. Тред по id сообщения в
 *    Коммуникаторе не открыть — ему нужна группа, а её у ссылки нет;
 *  - `task` → карточка задачи в таск-трекере (режим «команда»: автор
 *    портрета не обязательно исполнитель, закрытые — тоже);
 *  - `attendance` / `mark` → карточка группы (`id` — группа). Маршрут карточки
 *    группы есть у всех ролей портрета;
 *  - `survey` / `agreement` / `payment` / `invoice` → своя вкладка карточки;
 *  - `funding` — простым текстом: у заявки дофинансирования нет своего
 *    адреса, а раздел у части ролей портрета закрыт;
 *  - `aplora_lead` / `aplora_call` (ТЗ 0120) → карточка лида на вкладке
 *    «Aplora». `leadId` сервер отдаёт только ролям лидов; без него — текст.
 */
export const aiEvidenceHref = (
  ref: Pick<AiEvidenceRef, 'kind' | 'id'> & { leadId?: string | null },
  studentId: string,
  access: IAiEvidenceAccess,
): string | null => {
  const id = (ref.id ?? '').trim();
  switch (ref.kind) {
    case 'message':
    case 'sms':
    case 'note':
      return access.canSeeCommunicationTab
        ? aiStudentTabHref(studentId, 'communication')
        : null;
    case 'task':
      return id && access.canOpenTasks
        ? dashboardLink({
            view: 'tasks',
            taskMode: 'team',
            taskFilter: 'all',
            withClosed: true,
            task: id,
          })
        : null;
    case 'attendance':
    case 'mark':
      return id ? PATHS.groupDetail.replace(':id', id) : null;
    case 'survey':
      return access.canSeeSurveysTab
        ? aiStudentTabHref(studentId, 'surveys')
        : null;
    case 'agreement':
      return access.canSeeAgreementsTab
        ? aiStudentTabHref(studentId, 'agreements')
        : null;
    case 'payment':
      return access.canSeePaymentsTab
        ? aiStudentTabHref(studentId, 'payments')
        : null;
    case 'invoice':
      return access.canSeeInvoicesTab
        ? aiStudentTabHref(studentId, 'invoices')
        : null;
    case 'aplora_lead':
    case 'aplora_call': {
      const leadId = (ref.leadId ?? '').trim();
      return leadId && access.canOpenLeads ? aiLeadAploraHref(leadId) : null;
    }
    default:
      return null;
  }
};

// ── Время ──────────────────────────────────────────────────────────────────

/** Дата и время портрета: «09.10.2026 14:05». */
export const formatAiPortraitDateTime = (
  iso: string | null | undefined,
): string | null => {
  if (!iso) return null;
  const parsed = moment(iso);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY HH:mm') : null;
};

/** Дата события доказательства: «01.10.2026». */
export const formatAiEvidenceDate = (
  iso: string | null | undefined,
): string | null => {
  if (!iso) return null;
  const parsed = moment(iso);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY') : null;
};
